import { faInstagram, faLinkedin, faYoutube } from "@fortawesome/free-brands-svg-icons";
import { IconDefinition, faCookieBite } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as flags from "country-flag-icons/react/3x2";
import { motion } from "framer-motion";
import { Link } from "gatsby";
import process from "process";
import { default as React, useEffect, useState } from "react";
import { LANGUAGE_FOR_LOCALE, Language, Locale } from "../../config";
import { Button } from "../shared/Button";
import { ColorBar } from "../shared/ColorBar";
import { Img } from "../shared/Img";
import { FlagForLocale, LocaleLanguage, isValidLocale, languageSwitch, locPath, makeI18n } from "../shared/Languages";
import { LinkAsButton, useGetCurrentSearch } from "../shared/LinkAsButton";
import { Section } from "../shared/Section";
import { useTracking } from "../shared/tracking/trackUser";
import { isExpansionLocale } from "./landing_page_expansion";
import { MirrorPageProps } from "./type";

export const makeNav = (newLocale: string, search: string) => {
    return `/${newLocale.toLowerCase()}/${search}`;
};

const useSuggestedLocale = (currentLocale: Locale) => {
    const [suggestedLocale, setSuggestedLocale] = useState<Locale | undefined>(undefined);
    const [prefferedLocale, setPrefferedLocale] = useLocalStorageLocale(`preferred-locale`, undefined);

    useEffect(() => {
        const branch = process.env.GATSBY_BRANCH;
        const isDev = process.env.NODE_ENV === "development";

        const favIconUrl = () => {
            if (isDev) {
                return `http://localhost:8788/favicon-32x32.png?r=${Math.random()}`;
            } else if (branch === "main") {
                return `${process.env.GATSBY_BASE_URL}/favicon-32x32.png?r=${Math.random()}`;
            } else {
                return `https://staging.website-com-1oh.pages.dev/favicon-32x32.png?r=${Math.random()}`;
            }
        };
        fetch(favIconUrl())
            .then((t) => {
                const localeHeader = t.headers.get("Suggested-Locale")?.toUpperCase() ?? "";
                if (isValidLocale(localeHeader)) {
                    setSuggestedLocale(localeHeader as Locale);
                    console.warn(`Set suggested locale ${localeHeader}`);
                } else {
                    console.warn("Failed to parse suggested country");
                }
            })
            .catch((e) => {
                console.error("Failed to fetch favicon");
                console.error(e);
            });
    }, []);

    const hasPreferredLocaleSet = !!prefferedLocale;
    const hasSuggestedLocale = !!suggestedLocale;
    const localeDiffers = suggestedLocale ? currentLocale !== suggestedLocale : false;

    const showLanguagePopup = hasSuggestedLocale && !hasPreferredLocaleSet && localeDiffers;
    const showLanguageSwitcher = !showLanguagePopup && (hasPreferredLocaleSet || localeDiffers);

    return {
        suggestedLocale: suggestedLocale,
        showLanguagePopup,
        showLanguageSwitcher,
        // suggestedLocale could be found & no preference was set & the suggestedLocale differs from the current Locale
        //
        setPrefferedLocale: setPrefferedLocale,
    };
};

const useLocalStorageLocale = (key: string, initial?: string): [string | undefined, (state: string) => void] => {
    const [locale, _setFlag] = useState<string | undefined>();

    useEffect(() => {
        _setFlag(() => {
            const lsItem = localStorage.getItem(key);
            if (lsItem == null) return initial ?? undefined;
            if (!isValidLocale(lsItem)) return initial ?? undefined;
            return lsItem as string;
        });
    });

    const setLocale = (newLocale: string) => {
        _setFlag(newLocale);
        localStorage.setItem(key, newLocale.toString());
    };
    return [locale, setLocale];
};

const SUPPORTED_COUNTRIES = [
    "DE-DE",
    "DE-AT",
    "DE-CH",
    "EN-GB",
    "FR-FR",
    "PT-PT",
    // "RO-RO",
    "IT-IT",
    "PT-BR",
    // "SV-SE",
    "ES-ES",
    // "PL-PL",
    "NL-NL",
    "FR-BE",
    "NL-BE",
] as const satisfies Locale[];

const ALL_COUNTRIES = {
    "DE-DE": {
        flag: "DE",
        name: (language) =>
            languageSwitch(language, {
                DE: "Deutschland",
                EN: "Germany",
                SV: "Tyskland",
                PTBR: "Alemanha",
                PT: "Alemanha",
                PL: "Niemcy",
                ES: "Alemania",
                CZ: "Německo",
                FR: "Allemagne",
                IT: "Germania",
                RO: "Germania",
                NL: "Duitsland",
            }),
    },
    "DE-AT": {
        flag: "AT",
        name: (language) =>
            languageSwitch(language, {
                DE: "Österreich",
                EN: "Austria",
                SV: "Österrike",
                PTBR: "Áustria",
                PT: "Áustria",
                PL: "Austria",
                ES: "Austria",
                CZ: "Rakousko",
                FR: "Autriche",
                IT: "Austria",
                RO: "Austria",
                NL: "Oostenrijk",
            }),
    },
    "DE-CH": {
        flag: "CH",
        name: (language) =>
            languageSwitch(language, {
                DE: "Schweiz",
                EN: "Switzerland",
                SV: "Schweiz",
                PTBR: "Suíça",
                PT: "Suíça",
                PL: "Szwajcaria",
                ES: "Suiza",
                CZ: "Švýcarsko",
                FR: "Suisse",
                IT: "Svizzera",
                RO: "Elveția",
                NL: "Zwitserland",
            }),
    },
    "EN-GB": {
        flag: "GB",
        name: (language) =>
            languageSwitch(language, {
                DE: "Vereinigtes Königreich",
                EN: "United Kingdom",
                SV: "Förenade kungariket",
                PTBR: "Reino Unido",
                PT: "Reino Unido",
                PL: "Wielka Brytania",
                ES: "Reino Unido",
                CZ: "Spojené království",
                FR: "Royaume-Uni",
                IT: "Regno Unito",
                RO: "Regatul Unit",
                NL: "Verenigd Koninkrijk",
            }),
    },
    "PL-PL": {
        flag: "PL",
        name: (language) =>
            languageSwitch(language, {
                DE: "Polen",
                EN: "Poland",
                SV: "Polen",
                PTBR: "Polônia",
                PT: "Polónia",
                PL: "Polska",
                ES: "Polonia",
                CZ: "Polsko",
                FR: "Pologne",
                IT: "Polonia",
                RO: "Polonia",
                NL: "Polen",
            }),
    },
    "ES-ES": {
        flag: "ES",
        name: (language) =>
            languageSwitch(language, {
                DE: "Spanien",
                EN: "Spain",
                SV: "Spanien",
                PTBR: "Espanha",
                PT: "Espanha",
                PL: "Hiszpania",
                ES: "España",
                CZ: "Španělsko",
                FR: "Espagne",
                IT: "Spagna",
                RO: "Spania",
                NL: "Spanje",
            }),
    },
    "CZ-CZ": {
        flag: "CZ",
        name: (language) =>
            languageSwitch(language, {
                DE: "Tschechische Republik",
                EN: "Czech Republic",
                SV: "Tjeckien",
                PTBR: "República Tcheca",
                PT: "República Checa",
                PL: "Republika Czeska",
                ES: "República Checa",
                CZ: "Česká republika",
                FR: "République tchèque",
                IT: "Repubblica Ceca",
                RO: "Republica Cehă",
                NL: "Tsjechië",
            }),
    },
    "FR-FR": {
        flag: "FR",
        name: (language) =>
            languageSwitch(language, {
                DE: "Frankreich",
                EN: "France",
                SV: "Frankrike",
                PTBR: "França",
                PT: "França",
                PL: "Francja",
                ES: "Francia",
                CZ: "Francie",
                FR: "France",
                IT: "Francia",
                RO: "Franța",
                NL: "Frankrijk",
            }),
    },
    "IT-IT": {
        flag: "IT",
        name: (language) =>
            languageSwitch(language, {
                DE: "Italien",
                EN: "Italy",
                SV: "Italien",
                PTBR: "Itália",
                PT: "Itália",
                PL: "Włochy",
                ES: "Italia",
                CZ: "Itálie",
                FR: "Italie",
                IT: "Italia",
                RO: "Italia",
                NL: "Italië",
            }),
    },
    "PT-PT": {
        flag: "PT",
        name: (language) =>
            languageSwitch(language, {
                DE: "Portugal",
                EN: "Portugal",
                SV: "Portugal",
                PTBR: "Portugal",
                PT: "Portugal",
                PL: "Portugalia",
                ES: "Portugal",
                CZ: "Portugalsko",
                FR: "Portugal",
                IT: "Portogallo",
                RO: "Portugalia",
                NL: "Portugal",
            }),
    },
    "PT-BR": {
        flag: "BR",
        name: (language) =>
            languageSwitch(language, {
                DE: "Brasilien",
                EN: "Brasil",
                SV: "Brasilien",
                PTBR: "Brasil",
                PT: "Brasil",
                PL: "Brazylia",
                ES: "Brasil",
                CZ: "Brasil",
                FR: "Brésil",
                IT: "Brasile",
                RO: "România",
                NL: "Brazilië",
            }),
    },
    "SV-SE": {
        flag: "SE",
        name: (language) =>
            languageSwitch(language, {
                DE: "Schweden",
                EN: "Sweden",
                SV: "Sverige",
                PTBR: "Suécia",
                PT: "Suécia",
                PL: "Szwecja",
                ES: "Suecia",
                CZ: "Švédsko",
                FR: "Suède",
                IT: "Svezia",
                RO: "România",
                NL: "Zweden",
            }),
    },
    "RO-RO": {
        flag: "RO",
        name: (language) =>
            languageSwitch(language, {
                DE: "Rumänien",
                EN: "Romania",
                SV: "Rumänien",
                PTBR: "Roménia",
                PT: "Roménia",
                PL: "Rumunia",
                ES: "rumanía",
                CZ: "Rumunsko",
                FR: "Roumanie",
                IT: "Romania",
                RO: "România",
                NL: "Roemenië",
            }),
    },
    "EL-GR": {
        flag: "GR",
        name: () => "Greece",
    },
    "ES-MX": {
        flag: "MX",
        name: () => "Mexico",
    },
    "ET-EE": {
        flag: "EE",
        name: () => "Estonia",
    },
    "FR-BE": {
        flag: "BE",
        name: () => "Belgium (FR)",
    },
    "LT-LT": {
        flag: "LT",
        name: () => "Lithuania",
    },
    "LV-LV": {
        flag: "LV",
        name: () => "Latvia",
    },
    "NL-BE": {
        flag: "BE",
        name: () => "Belgium (NL)",
    },
    "NL-NL": {
        flag: "NL",
        name: () => "Netherlands",
    },
    "TR-TR": {
        flag: "TR",
        name: () => "Turkey",
    },
    "FR-CA": {
        flag: "CA",
        name: () => "Canada (FR)",
    },
    "EN-CA": {
        flag: "CA",
        name: () => "Canada (EN)",
    },
    "FR-TN": {
        flag: "TN",
        name: () => "Tunisia (FR)",
    },
    "FR-MA": {
        flag: "MA",
        name: () => "Morocco (FR)",
    },
} satisfies Record<
    Locale,
    {
        flag: keyof typeof flags;
        // eslint-disable-next-line no-restricted-globals
        name: (lang: Exclude<Language, "EL" | "TR" | "LT" | "ET" | "LV" | "CA">) => string;
    }
>;

export const Footer = ({ locale: currentLocale, path, params }: MirrorPageProps) => {
    if (
        ["TR-TR", "EL-GR", "FR-BE", "NL-BE", "NL-NL", "TR-TR", "ES-MX", "ET-EE", "LV-LV", "LT-LT"].includes(
            currentLocale,
        )
    ) {
        currentLocale = "EN-GB";
    }
    const language =
        LocaleLanguage[
            currentLocale as Exclude<
                Locale,
                "TR-TR" | "EL-GR" | "FR-BE" | "NL-BE" | "NL-NL" | "TR-TR" | "ES-MX" | "ET-EE" | "LV-LV" | "LT-LT"
            >
        ];
    const { i18n } = makeI18n(language);

    const FooterLink = (props: { href: string; icon?: IconDefinition; text: string }) => {
        // return nothing if not implemented, else we have a bunch of 404 links for not implemented pages
        if (!props.text || !props.text.length || !props.href) {
            return <></>;
        }
        return (
            <a
                className="py-1 hover:text-gray-500 whitespace-nowrap flex items-center gap-2"
                href={props.href}
                aria-label={props.text}
            >
                {props.icon && <FontAwesomeIcon icon={props.icon} />}
                {props.text}
            </a>
        );
    };

    const search = useGetCurrentSearch();
    const LanguageButton = (props: {
        flag: keyof typeof flags;
        name: string;
        selected: boolean;
        locale: Locale;
        onClick?: () => void;
        search: string;
    }) => {
        return (
            <a
                className={`
                    rounded-lg flex items-center gap-1.5 px-1.5 py-1.5 justify-start transition-all
                    ${props.selected ? "bg-gray-200 hover:bg-gray-300" : "hover:bg-gray-300"}
                `}
                onClick={() => props.onClick && props.onClick()}
                href={makeNav(props.locale, search)}
            >
                <div className="w-6 h-4 rounded-sm shadow-md overflow-hidden">
                    {flags[props.flag]({ className: "w-6 h-4" })}
                </div>
                {props.name}
            </a>
        );
    };

    const { setConsent, showPopup: showConsentPopup } = useTracking();

    const {
        showLanguageSwitcher, //
        suggestedLocale,
        showLanguagePopup,
        setPrefferedLocale,
    } = useSuggestedLocale(currentLocale);

    return (
        <>
            {/* <TrackPageVisit locale={currentLocale} /> */}
            {/* Popup for cookies */}
            {showLanguagePopup === false && showConsentPopup && (
                <div
                    className="
                    fixed z-[9999] left-2 md:left-5 bottom-2 md:bottom-5 w-[calc(100%-16px)] md:w-fit 
                    md:max-w-[600px] bg-white/90 backdrop-blur rounded-[28px] shadow-lg
                "
                >
                    <div className="flex w-full px-8 py-8 gap-5">
                        <div className="mt-1 hidden md:flex ml-1 text-3xl w-12 h-12 bg-gray-200 rounded-[12px] items-center justify-center shrink-0">
                            <FontAwesomeIcon icon={faCookieBite} />
                        </div>
                        <div className="flex flex-col">
                            <div className="w-full md:w-auto mb-4">
                                <div className="font-bold md:text-2xl text-xl">
                                    {i18n({
                                        DE: "Wir nutzen Cookies",
                                        EN: "We use cookies",
                                        SV: "Vi använder cookies",
                                        PTBR: "Usamos cookies",
                                        PT: "Utilizamos cookies",
                                        PL: "Używamy plików cookie",
                                        ES: "Utilizamos cookies",
                                        CZ: "Používáme soubory cookie",
                                        FR: "Nous utilisons des cookies",
                                        IT: "Utilizziamo i cookie",
                                        RO: "Folosim cookie-uri",
                                    })}
                                </div>
                                <div className="text-xs md:text-sm">
                                    {i18n({
                                        DE: "um besser zu verstehen, wie Sie unsere Webseite nutzen. Sie möchten einen richtigen Cookie? Dann stehen die Chancen in unserem Büro besser. Kommen Sie gerne vorbei!",
                                        EN: "to better understand how you use our website. Would you like a real cookie? Then the chances are better in our office. Feel free to drop by!",
                                        SV: "för att bättre förstå hur du använder vår webbplats. Vill du ha en riktig cookie? Då är chansen större på vårt kontor. Kom gärna förbi!",
                                        PTBR: "para entender melhor como você usa nosso site. Você gostaria de um cookie de verdade? Então as chances são maiores em nosso escritório. Fique à vontade para nos visitar!",
                                        PT: "para compreender melhor a forma como utiliza o nosso website. Apetece-lhe uma cookie verdadeira? O mais provável é que tenhamos algumas no escritório. Não hesite em passar por cá!",
                                        PL: "aby lepiej zrozumieć, w jaki sposób użytkownik korzysta z naszej witryny. Chcesz otrzymać prawdziwe ciasteczko? Wtedy szanse są większe w naszym biurze.Zapraszamy!",
                                        ES: "para comprender mejor cómo utiliza nuestro sitio web. ¿Quiere una galleta de verdad? Entonces las posibilidades son mayores en nuestra oficina. Nodude en pasarse por aquí.",
                                        CZ: "abychom lépe porozuměli tomu, jak naše webové stránky používáte. Chcete skutečný soubor cookie? Pak je větší šance v naší kanceláři. Neváhejte azastavte se u nás!",
                                        FR: "pour mieux comprendre comment vous utilisez notre site web. Vous voulez un vrai cookie ? Les chances sont meilleures dans notre bureau. N'hésitez pas àpasser nous voir !",
                                        IT: "per capire meglio come utilizzate il nostro sito web. Volete un vero biscotto? Allora le possibilità sono maggiori nel nostro ufficio. Passate pure atrovarci!",
                                        RO: "pentru a înțelege mai bine cum utilizați site-ul nostru web. Doriți un cookie adevărat? Atunci șansele sunt mai mari la biroul nostru. Nu ezitați să ne vizitați!",
                                    })}
                                </div>
                                <div className="text-gray-500 text-sm gap-3 flex mt-1 flex-wrap">
                                    <Link className="whitespace-nowrap" to={locPath(currentLocale, "/legal/cookies/")}>
                                        {i18n({
                                            DE: "Einstellungen",
                                            EN: "Settings",
                                            SV: "Inställningar",
                                            PTBR: "Configurações",
                                            PT: "Definições",
                                            PL: "Dostosowywanie ustawień",
                                            ES: "Personalizar la configuración",
                                            CZ: "Přizpůsobení nastavení",
                                            FR: "Personnaliser les paramètres",
                                            IT: "Personalizzazione delle impostazioni",
                                            RO: "Setări",
                                        })}
                                    </Link>

                                    <Link
                                        className="whitespace-nowrap"
                                        to={locPath(currentLocale, "/legal/dataprotection/")}
                                    >
                                        {i18n({
                                            DE: "Datenschutz",
                                            EN: "Data Protection",
                                            SV: "Dataskydd",
                                            PTBR: "Proteção de dados",
                                            PT: "Proteção de dados",
                                            PL: "Ochrona danych",
                                            ES: "Protección de datos",
                                            CZ: "Ochrana údajů",
                                            FR: "Protection des données",
                                            IT: "Protezione dei dati",
                                            RO: "Protecția datelor",
                                        })}
                                    </Link>

                                    <Link className="whitespace-nowrap" to={locPath(currentLocale, "/legal/imprint/")}>
                                        {i18n({
                                            DE: "Impressum",
                                            EN: "Legal notice",
                                            SV: "Juridiskt meddelande",
                                            PTBR: "Aviso legal",
                                            PT: "Informação legal",
                                            PL: "Nadruk",
                                            ES: "Pie de imprenta",
                                            CZ: "Otisk",
                                            FR: "Mentions légales",
                                            IT: "Impronta",
                                            RO: "Mentionare legală",
                                        })}
                                    </Link>
                                </div>
                            </div>

                            <div className="flex flex-wrap xs:flex-nowrap gap-1 justify-between md:justify-end md:gap-2">
                                <Button
                                    className="w-full text-gray-700 bg-gray-100 hover:bg-gray-200"
                                    onClick={() => setConsent(false)}
                                >
                                    {i18n({
                                        DE: "Nur notwendige",
                                        EN: "Only essential",
                                        SV: "Endast väsentliga",
                                        PTBR: "Apenas essencial",
                                        PT: "Só essenciais",
                                        PL: "Tylko niezbędne",
                                        ES: "Sólo esencial",
                                        CZ: "Pouze nezbytné",
                                        FR: "Uniquement indispensable",
                                        IT: "Solo l'essenziale",
                                        RO: "Doar esențiale",
                                    })}
                                </Button>
                                <Button
                                    className="w-full bg-reonic-yellow text-gray-800 hover:bg-reonic-yellow/80"
                                    onClick={() => setConsent(true)}
                                >
                                    {i18n({
                                        DE: "Alle akzeptieren",
                                        EN: "Accept all",
                                        SV: "Acceptera alla",
                                        PTBR: "Aceitar tudo",
                                        PT: "Aceitar tudo",
                                        PL: "Zaakceptuj wszystko",
                                        ES: "Aceptar todo",
                                        CZ: "Přijmout všechny",
                                        FR: "Accepter tous",
                                        IT: "Accettare tutti",
                                        RO: "Acceptați toate",
                                    })}
                                </Button>
                            </div>
                        </div>

                        <div className="" />
                    </div>
                </div>
            )}

            {/* Language popup */}
            {/* Ask user to switch to correct locale */}
            {showLanguagePopup && suggestedLocale && (
                <div
                    className="
                    fixed z-[9999] left-2 md:left-5 bottom-2 md:bottom-5 w-[calc(100%-16px)] md:w-fit 
                    md:max-w-[600px] bg-white/90 backdrop-blur rounded-[28px] shadow-lg
                "
                >
                    <div className="flex w-full p-8 sm:gap-5 gap-2 flex-col">
                        <div className="pb-2">
                            <div className="font-semibold md:text-lg text-sm">
                                {languageSwitch(LocaleLanguage[suggestedLocale], {
                                    DE: "Du besuchst unsere Seite aus Deutschland.",
                                    EN: "You are accessing our page from the UK.",
                                    SV: "Du besöker vår sida från Storbritannien.",
                                    PTBR: "Você está acessando nossa página do Reino Unido.",
                                    PT: "Está a aceder à nossa página a partir de Portugal",
                                    PL: "Wykryliśmy, że odwiedzasz naszą stronę z Polski.",
                                    CZ: "Navštěvujete naše webové stránky z České republiky.",
                                    ES: "Nos visita desde España.",
                                    FR: "Tu visites notre site depuis la France.",
                                    IT: "State visitando il nostro sito dall'Italia.",
                                    RO: "Vizitați site-ul nostru din România.",
                                    EL: "Έχετε πρόσβαση στη σελίδα μας από την Ελλάδα.",
                                    ET: "Sa sisened meie lehele Eestist.",
                                    LT: "Jūs lankotės mūsų puslapyje iš Lietuvos.",
                                    LV: "Jūs atverat mūsu lapu no Latvijas.",
                                    NL: "U bezoekt onze pagina vanuit Nederland.",
                                    TR: "Sayfamıza Türkiye'den erişiyorsunuz.",
                                })}
                            </div>
                            <div className="sm:text-md text-sm">
                                {languageSwitch(LocaleLanguage[suggestedLocale], {
                                    DE: "Möchtest du auf die deutsche Webseite wechseln?",
                                    EN: "Do you want to change to the English Webpage?",
                                    SV: "Vill du byta till den engelska webbsidan?",
                                    PTBR: "Você quer mudar para a página da Web em inglês?",
                                    PT: "Deseja mudar para a página em português?",
                                    PL: "Czy chcesz zmienić wersję na polską?",
                                    ES: "¿Desea cambiar al sitio web en español?",
                                    CZ: "Chcete přejít na české webové stránky?",
                                    FR: "Tu veux aller sur le site français?",
                                    IT: "Volete passare al sito italiano?",
                                    RO: "Vrei să te întoarci pe pagina noastră în limba română?",
                                    EL: "Θέλετε να αλλάξετε στην ελληνική ιστοσελίδα;",
                                    ET: "Kas soovite vahetada estionlaste veebilehte?",
                                    LT: "Ar norite pereiti į lietuvišką tinklalapį?",
                                    LV: "Vai vēlaties pāriet uz latviešu tīmekļa vietni?",
                                    NL: "Wilt u overschakelen naar de Nederlandse webpagina?",
                                    TR: "Türkçe web sayfasına geçmek istiyor musunuz?",
                                })}
                            </div>
                        </div>
                        <div className="flex flex-wrap xs:flex-nowrap gap-1 justify-between md:justify-end md:gap-2">
                            <Button
                                onClick={() => {
                                    setPrefferedLocale(currentLocale);
                                }}
                                className="w-full text-gray-700 bg-gray-100 hover:bg-gray-200"
                            >
                                {languageSwitch(LocaleLanguage[suggestedLocale], {
                                    DE: "Hier bleiben",
                                    EN: "Stay here",
                                    SV: "Stanna här",
                                    PTBR: "Fique aqui",
                                    PT: "Ficar aqui",
                                    PL: "Nie zmieniaj",
                                    ES: "No cambie",
                                    CZ: "Neměňte",
                                    FR: "Ne pas changer",
                                    IT: "Non cambiare",
                                    RO: "Rămâi aici",
                                    EL: "Μείνετε εδώ",
                                    ET: "Jää siia.",
                                    LT: "Apsistokite čia",
                                    LV: "Palieciet šeit",
                                    NL: "Blijf hier",
                                    TR: "Burada kalın.",
                                })}
                            </Button>
                            <LinkAsButton
                                className="w-full bg-reonic-yellow text-gray-800 hover:bg-reonic-yellow/80 justify-center"
                                onClick={() => {
                                    setPrefferedLocale(suggestedLocale);
                                }}
                                to={makeNav(suggestedLocale, search)}
                            >
                                <div className="w-6 h-4 mr-1 rounded-sm shadow-md overflow-hidden">
                                    <FlagForLocale mode="country" locale={suggestedLocale} className={"w-6 h-4"} />
                                </div>
                                {languageSwitch(LocaleLanguage[suggestedLocale], {
                                    DE: "Jetzt wechseln",
                                    EN: "Switch now",
                                    SV: "Byt nu",
                                    PTBR: "Troque agora",
                                    PT: "Mudar agora",
                                    PL: "Zmień teraz",
                                    ES: "Cambiar ahora",
                                    CZ: "Změnit nyní",
                                    FR: "Changer maintenant",
                                    IT: "Cambia ora",
                                    RO: "Schimba acum",
                                    EL: "Αλλάξτε τώρα",
                                    ET: "Lülita nüüd ümber",
                                    LT: "Perjunkite dabar",
                                    LV: "Pārslēdziet tagad",
                                    NL: "Nu overschakelen",
                                    TR: "Şimdi değiştir",
                                })}
                            </LinkAsButton>
                        </div>
                    </div>
                </div>
            )}

            {/* Language switcher */}
            {showLanguageSwitcher && suggestedLocale && !showConsentPopup && (
                <LanguageSwitcher currentLocale={currentLocale} setPrefferedLocale={setPrefferedLocale} />
            )}

            <div className="fixed -bottom-[1px] w-full hidden print:block">
                <Section className="grid-cols-2 text-xs hidden print:grid pb-6 pt-6" outerClassName="">
                    <div className="">
                        <div className="font-bold">Reonic GmbH</div>
                        <div>Provinostraße 52, 86153 Augsburg, Deutschland</div>
                    </div>
                    <div className="h-full flex flex-col justify-end items-end">
                        <div>+49 155 60717708</div>
                        <div>kontakt@reonic.de</div>
                    </div>
                </Section>
                <ColorBar className="" />
            </div>
            <Section
                className="
                    pb-20 pt-14 grid grid-cols-1 xs:grid-cols-[200px_1fr] sm:grid-cols-[200px_1fr_1fr] md:grid-cols-[200px_1fr_1fr_1fr] xl:grid-cols-[200px_1fr_1fr_1fr_1fr_1fr] print:hidden
                    gap-5 sm:gap-10 md:gap-20 
                "
                outerClassName="bg-zinc-100"
            >
                <div className="text-xs flex flex-col gap-1 row-span-5">
                    <Img className="w-[65px]" alt="company logo" src={"/images/logo.svg"} size="sm" sizeMobile="sm" />
                    <br />

                    {SUPPORTED_COUNTRIES.map((locale) => {
                        const e = ALL_COUNTRIES[locale];

                        return (
                            <LanguageButton
                                search={search}
                                flag={e.flag}
                                key={locale}
                                name={e.name(language)}
                                selected={currentLocale === locale}
                                locale={locale}
                            />
                        );
                    })}
                    <br />
                    <div className="font-medium text-sm mb-1 mt-4">
                        {i18n({
                            DE: "Standort Augsburg",
                            EN: "Location Augsburg",
                            SV: "Plats Augsburg",
                            PTBR: "Localização Augsburg",
                            PT: "Augsburg",
                            PL: "Lokalizacja Augsburg",
                            ES: "Ubicación de Augsburgo",
                            CZ: "Umístění v Augsburgu",
                            FR: "Site d'Augsbourg",
                            IT: "Sede di Augsburg",
                            RO: "Locul de lucru Augsburg",
                        })}
                    </div>
                    <div>Provinostraße 52</div>
                    <div>86153 Augsburg</div>
                    <div>
                        {i18n({
                            DE: "Deutschland",
                            EN: "Germany",
                            SV: "Tyskland",
                            PTBR: "Alemanha",
                            PT: "Alemanha",
                            PL: "Niemcy",
                            ES: "Alemania",
                            CZ: "Německo",
                            FR: "Allemagne",
                            IT: "Germania",
                            RO: "România",
                        })}
                    </div>
                    <div className="mt-2" />
                    <div className="font-medium text-sm mb-1 mt-4">
                        {i18n({
                            DE: "Standort Berlin",
                            EN: "Location Berlin",
                            SV: "Plats Berlin",
                            PTBR: "Localização em Berlim",
                            PT: "Berlim",
                            PL: "Lokalizacja w Berlinie",
                            ES: "Ubicación en Berlín",
                            CZ: "Umístění v Berlíně",
                            FR: "Site de Berlin",
                            IT: "Sede di Berlino",
                            RO: "Locul de lucru Berlin",
                        })}
                    </div>
                    <div>Rosenstraße 17</div>
                    <div>10178 Berlin</div>
                    <div>
                        {i18n({
                            DE: "Deutschland",
                            EN: "Germany",
                            SV: "Tyskland",
                            PTBR: "Alemanha",
                            PT: "Alemanha",
                            PL: "Niemcy",
                            ES: "Alemania",
                            CZ: "Německo",
                            FR: "Allemagne",
                            IT: "Germania",
                            RO: "România",
                        })}
                    </div>

                    <br />
                    <div>Reonic GmbH</div>
                    <div>Amtsgericht Augsburg</div>
                    <div>HRB 36147</div>
                    <div>DE342755511</div>
                    <br />
                    <div>
                        {languageSwitch(language, {
                            DE: "+49 155 60717708",
                            EN: "+49 155 60717708",
                            SV: "+49 155 60717708",
                            PT: "+49 155 60717708",
                            PL: "+49 155 60717708",
                            ES: "+49 155 60717708",
                            CZ: "+49 155 60717708",
                            FR: "+33 1 89 71 84 98",
                            IT: "+49 155 60717708",
                            PTBR: "+49 155 60717708",
                            RO: "+49 155 60717708",
                        })}
                    </div>

                    <div>
                        {languageSwitch(language, {
                            DE: "kontakt@reonic.de",
                            EN: "kontakt@reonic.de",
                            SV: "kontakt@reonic.de",
                            PT: "kontakt@reonic.de",
                            PL: "kontakt@reonic.de",
                            ES: "kontakt@reonic.de",
                            CZ: "kontakt@reonic.de",
                            FR: "contact@reonic.fr",
                            IT: "kontakt@reonic.de",
                            PTBR: "kontakt@reonic.de",
                            RO: "kontakt@reonic.de",
                        })}
                    </div>

                    {/* <TrackedExternalLink> */}
                    {/* <FontAwesomeIcon icon={}></FontAwesomeIcon></TrackedExternalLink> */}
                </div>
                {language === "DE" && (
                    <div className="text-xs flex flex-col gap-1 items-start">
                        <div className="font-medium text-sm mb-1.5">
                            {languageSwitch(language, {
                                DE: "Produkte",
                            })}
                        </div>
                        <FooterLink
                            href={locPath(currentLocale, "/product/360h/")}
                            text={languageSwitch(language, {
                                DE: "360° Haushalt",
                            })}
                        />
                        <FooterLink
                            href={locPath(currentLocale, "/product/360b/")}
                            text={languageSwitch(language, {
                                DE: "360° Gewerbekunden",
                            })}
                        />
                    </div>
                )}
                <div className="text-xs flex flex-col gap-1 items-start">
                    <div className="font-medium text-sm mb-1.5">
                        {languageSwitch(language, {
                            DE: "Lösungen",
                            EN: "Solutions",
                            SV: "Lösningar",
                            PTBR: "Soluções",
                            PT: "Soluções",
                            PL: "Rozwiązania",
                            ES: "Soluciones",
                            CZ: "Řešení",
                            FR: "Solutions",
                            IT: "Soluzioni",
                            RO: "Soluții",
                        })}
                    </div>
                    <FooterLink
                        href={locPath(currentLocale, "/industry/solar/")}
                        text={languageSwitch(language, {
                            DE: "Für Solarteure",
                            EN: "For solar installers",
                            SV: "För solcellsinstallatörer",
                            PTBR: "Para instaladores de energia solar",
                            PT: "Para instaladores de sistemas fotovoltaicos",
                            PL: "Dla instalatorów systemów solarnych",
                            ES: "Para instaladores solares",
                            CZ: "Pro instalatéry solárních zařízení",
                            FR: "Pour les installateurs solaires",
                            IT: "Per gli installatori solari",
                            RO: "Pentru instalatori solari",
                        })}
                    />
                    {language === "DE" && (
                        <FooterLink
                            href={locPath(currentLocale, "/industry/utilities/")}
                            text={languageSwitch(language, {
                                DE: "Für Stadtwerke",
                                EN: "",
                                SV: "",
                                PTBR: "",
                                PT: "",
                                PL: "",
                                ES: "",
                                CZ: "",
                                FR: "",
                                IT: "",
                                RO: "",
                            })}
                        />
                    )}
                    {language === "DE" && (
                        <FooterLink
                            href={locPath(currentLocale, "/industry/heating/")}
                            text={languageSwitch(language, {
                                DE: "Für Heizungsbauer",
                                EN: "",
                                SV: "",
                                PTBR: "",
                                PT: "",
                                PL: "",
                                ES: "",
                                CZ: "",
                                FR: "",
                                IT: "",
                                RO: "",
                            })}
                        />
                    )}
                </div>
                <div className="text-xs flex flex-col gap-1 items-start">
                    <div className="font-medium text-sm mb-1.5">
                        {languageSwitch(language, {
                            DE: "Ressourcen",
                            EN: "Resources",
                            SV: "Resurser",
                            PTBR: "Recursos",
                            PT: "Recursos",
                            PL: "Zasoby",
                            ES: "Recursos",
                            CZ: "Zdroje",
                            FR: "Ressources",
                            IT: "Risorse",
                            RO: "Resurse",
                        })}
                    </div>
                    <FooterLink href={"https://portal.reonic.de"} text="Login" />
                    {/* <FooterLink href={"https://portal.reonic.de/support"} text="Support" /> */}
                    {["DE-DE", "FR-FR", "IT-IT"].includes(currentLocale) && (
                        <div>
                            <FooterLink href={locPath(currentLocale, "/blog/")} text={"Blog"} />
                        </div>
                    )}
                    {/* <FooterLink href={'https://docs.reonic.de'} text="Login" /> */}
                </div>
                <div className="text-xs flex flex-col gap-1 items-start">
                    <div className="font-medium text-sm mb-1.5">
                        {languageSwitch(language, {
                            DE: "Weiteres",
                            EN: "More",
                            SV: "Mer om",
                            PTBR: "Mais",
                            PT: "Mais",
                            PL: "Więcej informacji",
                            ES: "Más información",
                            CZ: "Další informace",
                            FR: "Autre",
                            IT: "Ulteriori informazioni",
                            RO: "Mai mult",
                        })}
                    </div>
                    <FooterLink href={"https://www.linkedin.com/company/reonic/"} text="LinkedIn" icon={faLinkedin} />
                    <FooterLink href={"https://www.instagram.com/reonic_gmbh"} text="Instagram" icon={faInstagram} />
                    <FooterLink
                        href={languageSwitch(language, {
                            DE: "https://www.youtube.com/@Reonic_gmbh",
                            EN: "https://www.youtube.com/@Reonic_gmbh",
                            SV: "https://www.youtube.com/@Reonic_gmbh",
                            PTBR: "https://www.youtube.com/@Reonic_gmbh",
                            PT: "https://www.youtube.com/@Reonic_gmbh",
                            PL: "https://www.youtube.com/@Reonic_gmbh",
                            ES: "https://www.youtube.com/@Reonic_gmbh",
                            CZ: "https://www.youtube.com/@Reonic_gmbh",
                            FR: "https://youtube.com/playlist?list=PLVBEGFNUTuCK44i-CZltz85GkLTK610hx&si=sZXeGFEU9ssYPFAs",
                            IT: "https://www.youtube.com/@Reonic_gmbh",
                            RO: "https://www.youtube.com/@Reonic_gmbh",
                        })}
                        text="Youtube"
                        icon={faYoutube}
                    />
                    <FooterLink
                        href={locPath(currentLocale, "/about/us/")}
                        text={languageSwitch(language, {
                            DE: "Über uns",
                            EN: "About us",
                            SV: "Om oss",
                            PTBR: "Sobre nós",
                            PT: "Sobre nós",
                            PL: "O nas",
                            ES: "Quiénes somos",
                            CZ: "O nás",
                            FR: "À propos de nous",
                            IT: "Chi siamo",
                            RO: "Despre noi",
                        })}
                    />
                    <FooterLink
                        href={locPath(currentLocale, "/about/team/")}
                        text={languageSwitch(language, {
                            DE: "Team",
                            EN: "Team",
                            SV: "Team",
                            PTBR: "Equipe",
                            PT: "Equipa",
                            PL: "Zespół",
                            ES: "Equipo",
                            CZ: "Tým",
                            FR: "L'équipe",
                            IT: "Squadra",
                            RO: "Echipă",
                        })}
                    />
                    <FooterLink
                        href={locPath(currentLocale, "/about/jobs/")}
                        text={languageSwitch(language, {
                            DE: "Jobs",
                            EN: "Jobs",
                            SV: "Jobb",
                            PTBR: "Empregos",
                            PT: "Empregos",
                            PL: "Praca",
                            ES: "Empleo",
                            CZ: "Nabídky práce:",
                            FR: "Emplois",
                            IT: "Offerte di lavoro",
                            RO: "Joburi",
                        })}
                    />

                    {/* <FooterLink
                        href={"https://www.xing.com/pages/reonic-gmbh"}
                        text="Xing"
                        icon={faXing}
                    /> */}
                </div>
                <div className="text-xs flex flex-col gap-1">
                    <div className="font-medium text-sm mb-1.5">
                        {languageSwitch(language, {
                            DE: "Rechtliches",
                            EN: "Legal",
                            SV: "Juridisk",
                            PTBR: "Informações legais",
                            PT: "Informação Legal",
                            PL: "Kwestie prawne",
                            ES: "Asuntos jurídicos",
                            CZ: "Právní záležitosti",
                            FR: "Mentions légales",
                            IT: "Questioni legali",
                            RO: "Aspecte legale",
                        })}
                    </div>
                    <FooterLink
                        href={locPath(currentLocale, "/legal/imprint/")}
                        text={languageSwitch(language, {
                            DE: "Impressum",
                            EN: "Imprint",
                            SV: "Avtryck",
                            PTBR: "Impressão",
                            PT: "Informação legal",
                            PL: "Nadruk",
                            ES: "Pie de imprenta",
                            CZ: "Otisk",
                            FR: "Impression",
                            IT: "Impronta",
                            RO: "Imprimare",
                        })}
                    />
                    <FooterLink
                        href={locPath(currentLocale, "/legal/dataprotection/")}
                        text={languageSwitch(language, {
                            DE: "Datenschutz",
                            EN: "Data Protection",
                            SV: "Skydd av personuppgifter",
                            PTBR: "Proteção de dados",
                            PT: "Proteção de dados",
                            PL: "Ochrona danych",
                            ES: "Protección de datos",
                            CZ: "Ochrana údajů",
                            FR: "Protection des données",
                            IT: "Protezione dei dati",
                            RO: "Protecția datelor",
                        })}
                    />
                    <FooterLink href={locPath(currentLocale, "/legal/cookies/")} text="Cookies" />
                    <FooterLink
                        href={locPath(currentLocale, "/legal/terms/")}
                        text={languageSwitch(language, {
                            DE: "Geschäftsbedingungen",
                            EN: "Terms and conditions",
                            SV: "Villkor och anvisningar",
                            PTBR: "Termos e condições",
                            PT: "Termos e condições",
                            FR: "Conditions d'utilisation",
                            PL: "Zasady i warunki",
                            ES: "Condiciones generales",
                            CZ: "Podmínky a pravidla",
                            IT: "Termini e condizioni",
                            RO: "Condiții și termeni",
                        })}
                    />
                    <FooterLink
                        href={locPath(currentLocale, "/legal/dpa/")}
                        text={languageSwitch(language, {
                            DE: "Auftragsdatenverarbeitung",
                            EN: "Data processing agreement",
                            SV: "Avtal om databehandling",
                            PTBR: "Contrato de processamento de dados",
                            PT: "Acordo de tratamento de dados",
                            FR: "Accord sur le traitement des données",
                            PL: "Umowa o przetwarzanie danych",
                            ES: "Acuerdo de tratamiento de datos",
                            CZ: "Dohoda o zpracování údajů",
                            IT: "Accordo sul trattamento dei dati",
                            RO: "Acordarea de tratare a datelor",
                        })}
                    />
                </div>
                <div className="col-span-full mt-4 text-xs text-center">
                    Copyright © {new Date().getFullYear()} / Reonic GmbH / All rights reserved.
                </div>
            </Section>
        </>
    );
};

const LanguageSwitcher = ({
    currentLocale,
    setPrefferedLocale,
}: {
    currentLocale: Locale;
    setPrefferedLocale: (l: Locale) => void;
}) => {
    const currentLanguage = LANGUAGE_FOR_LOCALE[currentLocale];
    const [expandLanguagePopup, setExpandLanguagePopup] = useState(false);
    const search = useGetCurrentSearch();

    useEffect(() => {
        if (!expandLanguagePopup) return;
        const onClick = (e: MouseEvent) => {
            const lc = document.getElementById("language-container");
            if (!lc) return;
            if (!lc.contains(e.target as Node)) {
                setExpandLanguagePopup(false);
            }
        };
        document.addEventListener("click", onClick);
        return () => {
            document.removeEventListener("click", onClick);
        };
    }, [expandLanguagePopup]);

    return (
        <div
            className="
                fixed z-[9999] left-2 sm:left-5 bottom-2 sm:bottom-5 w-fit
              bg-white backdrop-blur shadow-xl overflow-hidden
              rounded-full sm:rounded-[24px]
            "
            id="language-container"
        >
            <div className="flex flex-col justify-end sm:w-full sm:h-auto sm:p-1 shrink-0">
                {SUPPORTED_COUNTRIES.map((m) => m)
                    .sort((a, b) => {
                        if (a === currentLocale) return 1;
                        else return -1;
                    })
                    .map((locale, index) => {
                        const expanded = locale === currentLocale || expandLanguagePopup;
                        const COUNTRY = ALL_COUNTRIES[locale];
                        return (
                            <motion.button
                                animate={expanded ? "open" : "closed"}
                                initial={false}
                                variants={{
                                    open: { marginBottom: 0 },
                                    PTBR: { marginBottom: 0 },
                                    closed: { marginBottom: -40 },
                                }}
                                transition={{ duration: 0.1 }}
                                className="
                                    language-pick flex gap-2 items-center p-2 sm:p-3 rounded-full 
                                    hover:bg-gray-100 h-10 w-[40px] sm:w-auto overflow-hidden shrink-0 grow-0
                                    bg-white text-sm font-medium
                                "
                                onClick={() => {
                                    setExpandLanguagePopup(!expandLanguagePopup);
                                    if (locale === currentLocale) return;
                                    setPrefferedLocale(locale);
                                    setTimeout(() => {
                                        window.location.href = makeNav(locale, search);
                                    }, 100);
                                }}
                                aria-label={`language-switch-${locale}`}
                            >
                                <FlagForLocale
                                    locale={locale}
                                    mode="country"
                                    className={"w-6 h-4 rounded-sm shadow-md"}
                                />
                                <span className="hidden sm:block">
                                    {isExpansionLocale(locale)
                                        ? // show switcher in english for not fully supported languages
                                          COUNTRY.name("EN")
                                        : COUNTRY.name(LANGUAGE_FOR_LOCALE[locale])}
                                </span>
                            </motion.button>
                        );
                    })}
            </div>
        </div>
    );
};
